import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import { getCellStatsRequested, getCellUpcomingBirthdaysRequested, getDashboardAnalyticsRequested } from "../../actions/cellAction";
import moment from "moment";
import { useNavigate, useOutletContext } from "react-router-dom";
import { UsersIcon, UserGroupIcon, HomeIcon, CakeIcon } from "@heroicons/react/solid";
import {
    ChartCard,
    CellGrowthChart,
    MemberGrowthChart,
    LeaderGrowthChart,
    AttendanceTrendChart
} from "../../components/Charts";
import { hasRole, ROLES } from "../../shared/Utils";

const DashboardHome = ({
    user,
    upcomingBirthdays,
    cellUpcomingBirthdaysGetRequested,
    cellStat,
    cellStatsGetRequested,
    dashboardAnalytics,
    dashboardAnalyticsGetRequested,
    getCellUpcomingBirthdaysRequested: getUpcomingBirthdays,
    getCellStatsRequested: getStats,
    getDashboardAnalyticsRequested: getAnalytics
}) => {
    const navigate = useNavigate();
    const { activeUser, activeCell } = useOutletContext() || {};
    const effectiveUser = activeUser || user;
    const isLeader = hasRole(effectiveUser, ROLES.CELL_LEADER);
    // Admin widgets only for the signed-in admin, not while impersonating a leader.
    const isAdmin = !activeUser && hasRole(effectiveUser, ROLES.ADMIN);
    const activeCellId = activeCell?.id;
    const lastFetchKeyRef = useRef(null);

    useEffect(() => {
        if (!effectiveUser?.id) {
            return;
        }
        // Refetch when the user (impersonation) or the selected cell changes.
        const fetchKey = `${effectiveUser.id}:${activeCellId || ''}:${activeUser ? 'imp' : ''}`;
        if (lastFetchKeyRef.current === fetchKey) {
            return;
        }
        lastFetchKeyRef.current = fetchKey;

        if (isLeader && activeCellId) {
            // Per-cell totals; the API also includes admin totals for admin+leader users.
            getStats(activeCellId);
            getUpcomingBirthdays(activeCellId);
        } else if (isAdmin) {
            getStats();
        }

        if (isAdmin) {
            getAnalytics();
        }
    }, [effectiveUser, activeUser, activeCellId, isLeader, isAdmin, getUpcomingBirthdays, getStats, getAnalytics]);

    if (!user || !user.id) {
        navigate('/login');
        return;
    }

    const today = moment();
    const nextTwoMonths = [today.format("MMMM")];
    for (let i = 0; i < 1; i++) {
        nextTwoMonths.push(today.add(1, "month").format("MMMM"));
    }
    const monthCaption = nextTwoMonths.join(" - ");

    const getMonthAndDay = (date) => {
        const dateObj = moment(date, "YYYY-MM-DD");
        const m = {
            month: dateObj.format("MMM"),
            day: dateObj.format("DD"),
        };
        return `${m.month}/${m.day}`;
    };

    const getInitials = (name) => {
        const names = name.split(" ");
        let initials = "";
        names.forEach((n) => {
            initials += n[0];
        });
        return initials;
    };

    const randomizeColor = () => {
        const colors = [
            "#FF6B6B", // light red
            "#77DD77", // pastel green
            "#AEC6CF", // pastel blue
            "#F49AC2", // pastel pink
            "#FDB813", // pastel yellow
            "#C5A3FF", // pastel purple
            "#FFC1CC", // pink
        ];
        const random = Math.floor(Math.random() * colors.length);
        return colors[random];
    };

    const name = effectiveUser?.name;
    const members_count = isLeader && cellStat?.cellMembersCount ? cellStat?.cellMembersCount : 0;
    const guests_count = isLeader && cellStat?.guestsCount ? cellStat?.guestsCount : 0;
    const leaders_count = isAdmin && cellStat?.cellLeaderCount ? cellStat?.cellLeaderCount : 0;
    const cell_count = isAdmin && cellStat?.cellCount ? cellStat?.cellCount : 0;

    return (
        <div className="w-full">
            <main className="space-y-6">
                {/* Welcome Header */}
                <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-6 shadow-sm">
                    <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                        Hello, {name}
                    </h1>
                    <p className="mt-2 text-base text-gray-600 sm:text-lg">
                        Here's what's happening with your Cell report account today.
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {isLeader && activeCell && (
                        <>
                            <StatCard
                                title={`Members · ${activeCell.name}`}
                                value={members_count}
                                icon={<UsersIcon className="h-6 w-6 text-white" />}
                                bgColor="bg-emerald-500"
                            />
                            <StatCard
                                title="Guests"
                                value={guests_count}
                                icon={<UserGroupIcon className="h-6 w-6 text-white" />}
                                bgColor="bg-blue-500"
                            />
                        </>
                    )}
                    {isAdmin && (
                        <>
                            <StatCard
                                title="Cell Leaders"
                                value={leaders_count}
                                icon={<UsersIcon className="h-6 w-6 text-white" />}
                                bgColor="bg-emerald-500"
                            />
                            <StatCard
                                title="Total Cells"
                                value={cell_count}
                                icon={<HomeIcon className="h-6 w-6 text-white" />}
                                bgColor="bg-blue-500"
                            />
                        </>
                    )}
                </div>

                {/* Analytics Charts - Admin Only */}
                {isAdmin && dashboardAnalytics && (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <ChartCard title="Cell Growth">
                            <CellGrowthChart data={dashboardAnalytics?.cellGrowth} />
                        </ChartCard>
                        <ChartCard title="Member Growth">
                            <MemberGrowthChart data={dashboardAnalytics?.memberGrowth} />
                        </ChartCard>
                        <ChartCard title="Leader Growth">
                            <LeaderGrowthChart data={dashboardAnalytics?.leaderGrowth} />
                        </ChartCard>
                        <ChartCard title="Attendance Trends">
                            <AttendanceTrendChart data={dashboardAnalytics?.attendanceTrends} />
                        </ChartCard>
                    </div>
                )}

                {/* Content Grid */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Upcoming Birthdays */}
                    {isLeader && activeCell && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-pink-500 rounded-full p-2">
                                            <CakeIcon className="h-5 w-5 text-white" />
                                        </div>
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Upcoming Birthdays
                                        </h2>
                                    </div>
                                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                        {monthCaption}
                                    </span>
                                </div>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {upcomingBirthdays.length > 0 ? (
                                    upcomingBirthdays.map((member, index) => (
                                        <div key={index} className="flex items-center p-4 hover:bg-gray-50 transition-colors">
                                            <div
                                                className="flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold text-sm mr-4"
                                                style={{ backgroundColor: randomizeColor() }}
                                            >
                                                {getInitials(member.name)}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900">{member.name}</p>
                                            </div>
                                            <div className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                                {getMonthAndDay(member.birthday)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                        <CakeIcon className="h-12 w-12 text-gray-300 mb-3" />
                                        <p>No upcoming birthdays</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </main>
        </div>
    );
}

// Stat Card Component
const StatCard = ({ title, value, icon, bgColor }) => {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`${bgColor} rounded-full p-3`}>
                    {icon}
                </div>
            </div>
        </div>
    );
};

DashboardHome.propTypes = {
    user: PropTypes.object,
    cellStat: PropTypes.object,
    cellStatsGetRequested: PropTypes.bool,
    upcomingBirthdays: PropTypes.array,
    cellUpcomingBirthdaysGetRequested: PropTypes.bool,
    dashboardAnalytics: PropTypes.object,
    dashboardAnalyticsGetRequested: PropTypes.bool,
    getCellStatsRequested: PropTypes.func,
    getCellUpcomingBirthdaysRequested: PropTypes.func,
    getDashboardAnalyticsRequested: PropTypes.func,
}

DashboardHome.defaultProps = {
    cellStatsGetRequested: false,
    cellUpcomingBirthdaysGetRequested: false,
    dashboardAnalyticsGetRequested: false,
    upcomingBirthdays: [],
    dashboardAnalytics: null,
};

const mapStateToProps = (state) => ({
    cellStat: state.cell.cellStat,
    cellStatsGetRequested: state.cell.cellStatsGetRequested,
    upcomingBirthdays: state.cell.cellUpcomingBirthdays,
    cellUpcomingBirthdaysGetRequested: state.cell.cellUpcomingBirthdaysGetRequested,
    dashboardAnalytics: state.cell.dashboardAnalytics,
    dashboardAnalyticsGetRequested: state.cell.dashboardAnalyticsGetRequested,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getCellStatsRequested,
    getCellUpcomingBirthdaysRequested,
    getDashboardAnalyticsRequested,
}, dispatch);
export default connect(mapStateToProps, mapDispatchToProps)(DashboardHome);